import GLightbox from 'glightbox';

/**
 * Opens a Vimeo video in a GLightbox popup (autoplay) for any element carrying
 * the `.js-video-lightbox` class. The Vimeo URL is read from the element's
 * `data-video` attribute (falling back to its href).
 *
 * We deliberately embed Vimeo's NATIVE player (a hand-built iframe) instead of
 * letting GLightbox wrap it in Plyr. With Vimeo, Plyr draws its own caption
 * overlay on top of Vimeo's native captions (double captions) and its CC toggle
 * is unreliable. Vimeo's native player gives a single, working CC button that is
 * off by default. GLightbox's own iframe path omits the `allow` attribute, so we
 * supply the markup via a `content` slide to keep autoplay/fullscreen working.
 */
export const videoLightbox = () => {
  const triggers = document.querySelectorAll('.js-video-lightbox');
  if (!triggers.length) return;

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();

      const rawUrl = trigger.dataset.video || trigger.getAttribute('href');
      if (!rawUrl || rawUrl.charAt(0) === '#') return;

      // Pull the Vimeo id and (optional) privacy hash out of any common URL
      // shape: vimeo.com/ID, vimeo.com/ID/HASH, player.vimeo.com/video/ID?h=HASH.
      const idMatch = rawUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      const hashMatch = rawUrl.match(
        /(?:vimeo\.com\/(?:video\/)?\d+\/|[?&]h=)([0-9a-zA-Z]+)/
      );
      const vimeoId = idMatch ? idMatch[1] : null;
      const vimeoHash = hashMatch ? hashMatch[1] : null;

      if (!vimeoId) return;

      // Private/unlisted videos require the `h=` hash. Captions stay off by
      // default — Vimeo's native CC button toggles them.
      const params = new URLSearchParams({
        autoplay: '1',
        title: '0',
        byline: '0',
        portrait: '0',
        dnt: '1',
      });
      if (vimeoHash) params.set('h', vimeoHash);

      const embedUrl = `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
      const content = `
        <div class="gvideo-vimeo" style="position:relative;width:100%;padding-top:56.25%;background:#000;">
          <iframe src="${embedUrl}" style="position:absolute;inset:0;width:100%;height:100%;border:0;" allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowfullscreen></iframe>
        </div>`;

      const lightbox = GLightbox({
        elements: [
          {
            content,
            width: 'min(90vw, 1000px)',
            height: 'auto',
            zoomable: false,
            draggable: false,
          },
        ],
      });

      // Tear the instance down once closed so repeat clicks stay in sync.
      lightbox.on('close', () => {
        setTimeout(() => lightbox.destroy(), 300);
      });

      lightbox.open();
    });
  });
};
